import { Box, Divider, Stack } from '@mui/material'
import { CircleIcon } from '@phosphor-icons/react'
import { ResponsivePie } from '@nivo/pie'
import {
    AreaValue,
    AreaValueDataObject,
    CardItem,
    monetaryToLocalizedString,
    MonetaryValueDataObjectType,
    PageGridContainer,
    PageGridItem,
    Percentage,
    tokens,
} from 'ComponentLibrary'
import { Typography } from 'ComponentLibrary/src/piazza'
import { TextWithEllipsis } from 'ComponentLibrary/src/piazza/text'
import { useTranslation } from 'react-i18next'
import { useCurrentResolvedLocale } from 'ClientPortal/src/i18n/currentLocale'

type rentKeyValuesType = {
    marketRent?: MonetaryValueDataObjectType
    contractualRent?: MonetaryValueDataObjectType
    rentDifference?: MonetaryValueDataObjectType
    rentDifferencePercentage?: number
}

type areaKeyValuesType = {
    totalArea?: AreaValueDataObject
    rentedArea?: AreaValueDataObject
    vacancyArea?: AreaValueDataObject
    rentedPercentage?: number
    vacancyPercentage?: number
}

type numberOfUnitsKeyValuesType = {
    totalUnits?: number
    rentedUnits?: number
    vacancyUnits?: number
    rentedPercentage?: number
    vacancyPercentage?: number
}

type TenantListKeyValuesProps = {
    rentKeyValues: rentKeyValuesType
    areaKeyValues: areaKeyValuesType
    numberOfUnitsKeyValues: numberOfUnitsKeyValuesType
}

export function TenantListKeyValues({ rentKeyValues, areaKeyValues, numberOfUnitsKeyValues }: TenantListKeyValuesProps) {
    const { t: tTenantListPage } = useTranslation('pages', { keyPrefix: 'assets.assetDetails.valuation.valuationTenantListPage.summary' })
    const locale = useCurrentResolvedLocale()

    return (
        <PageGridContainer>
            <PageGridItem columnSpan={6} columnSpanXs={4}>
                <CardItem display="flex" flexDirection="row" gap={tokens.SizingSpacing150} height="120px" alignItems="center">
                    <TenantListKeyValue
                        primary={{
                            color: tokens.ColorGraphSequentialOneMedium,
                            label: tTenantListPage('marketRentPerYear'),
                            value: monetaryToLocalizedString(rentKeyValues.marketRent, true, false, locale) ?? '-',
                        }}
                        secondary={{
                            color: tokens.ColorGraphSequentialOneLight,
                            label: tTenantListPage('delta'),
                            percentage: rentKeyValues.rentDifferencePercentage,
                            value: monetaryToLocalizedString(rentKeyValues.rentDifference, true, false, locale) ?? '-',
                        }}
                        total={{
                            label: tTenantListPage('contractRentPerYear'),
                            total: true,
                            value: monetaryToLocalizedString(rentKeyValues.contractualRent, true, false, locale) ?? '-',
                        }}
                        disabled={!rentKeyValues.marketRent?.amount && !rentKeyValues.contractualRent?.amount}
                    />

                    <Divider orientation="vertical" flexItem sx={{ borderColor: tokens.ColorBorderDefault }} />

                    <TenantListKeyValue
                        primary={{
                            color: tokens.ColorGraphSequentialTwoMedium,
                            label: tTenantListPage('rented'),
                            percentage: areaKeyValues.rentedPercentage,
                            value: areaKeyValues.rentedArea
                                ? AreaValue.fromDataObject(areaKeyValues.rentedArea).format({ withUnit: true, locale })
                                : '-',
                        }}
                        secondary={{
                            color: tokens.ColorGraphSequentialTwoLight,
                            label: tTenantListPage('vacant'),
                            percentage: areaKeyValues.vacancyPercentage,
                            value: areaKeyValues.vacancyArea
                                ? AreaValue.fromDataObject(areaKeyValues.vacancyArea).format({ withUnit: true, locale })
                                : '-',
                        }}
                        total={{
                            label: tTenantListPage('totalUsableArea'),
                            total: true,
                            value: areaKeyValues.totalArea
                                ? AreaValue.fromDataObject(areaKeyValues.totalArea).format({ withUnit: true, locale })
                                : '-',
                        }}
                        disabled={!areaKeyValues.totalArea?.value}
                    />
                    <Divider orientation="vertical" flexItem sx={{ borderColor: tokens.ColorBorderDefault }} />

                    <TenantListKeyValue
                        primary={{
                            color: tokens.ColorGraphSequentialThreeMedium,
                            label: tTenantListPage('rented'),
                            percentage: numberOfUnitsKeyValues.rentedPercentage,
                            value:
                                numberOfUnitsKeyValues.rentedUnits !== undefined
                                    ? tTenantListPage('parkingSpaces', { count: numberOfUnitsKeyValues.rentedUnits })
                                    : '-',
                        }}
                        secondary={{
                            color: tokens.ColorGraphSequentialThreeLight,
                            label: tTenantListPage('vacant'),
                            percentage: numberOfUnitsKeyValues.vacancyPercentage,
                            value:
                                numberOfUnitsKeyValues.vacancyUnits !== undefined
                                    ? tTenantListPage('parkingSpaces', { count: numberOfUnitsKeyValues.vacancyUnits })
                                    : '-',
                        }}
                        total={{
                            label: tTenantListPage('totalParkingSpaces'),
                            total: true,
                            value:
                                numberOfUnitsKeyValues.totalUnits !== undefined
                                    ? tTenantListPage('parkingSpaces', { count: numberOfUnitsKeyValues.totalUnits })
                                    : '-',
                        }}
                        disabled={!numberOfUnitsKeyValues.totalUnits}
                    />
                </CardItem>
            </PageGridItem>
        </PageGridContainer>
    )
}

type TenantListKeyValueProps = {
    primary: KeyValue
    secondary: KeyValue
    total: KeyValue
    disabled?: boolean
}

function TenantListKeyValue({ primary, secondary, total, disabled }: TenantListKeyValueProps) {
    const secondaryPercentage = secondary.percentage !== undefined ? secondary.percentage * 100 : 100
    const primaryPercentage = primary.percentage !== undefined ? primary.percentage * 100 : 100 - Math.abs(secondaryPercentage)

    const basePieData = [
        {
            id: primary.label,
            label: primary.label,
            value: primaryPercentage,
            color: primary.color ?? tokens.ColorSurfaceDefault,
        },
        {
            id: secondary.label,
            label: secondary.label,
            value: Math.abs(secondaryPercentage),
            color: secondary.color ?? tokens.ColorSurfaceDefault,
        },
    ]

    const pieData = disabled
        ? [{ id: 'disabled', label: 'disabled', value: 100, color: tokens.ColorGraphNeutralLightest }]
        : secondaryPercentage < 0
          ? [...basePieData].reverse()
          : basePieData

    return (
        <Stack flexGrow={1} direction="row" gap={tokens.SizingSpacing200} alignItems="center">
            <Box width="60px" height="60px">
                <ResponsivePie
                    data={pieData}
                    colors={{ datum: 'data.color' }}
                    innerRadius={0.35}
                    cornerRadius={4}
                    borderWidth={2}
                    borderColor={'#ffffff'}
                    enableArcLabels={false}
                    enableArcLinkLabels={false}
                    isInteractive={false}
                    animate={false}
                />
            </Box>
            <Stack direction="column" gap={tokens.SizingSpacing050} flexGrow={1}>
                <TenantListKeyValueRow {...primary} disabled={disabled} color={disabled ? tokens.ColorGraphNeutralLight : primary.color} />
                <TenantListKeyValueRow
                    {...secondary}
                    disabled={disabled}
                    color={disabled ? tokens.ColorGraphNeutralLightest : secondary.color}
                />
                <Divider sx={{ borderColor: tokens.ColorBorderDefault }} />
                <TenantListKeyValueRow {...total} disabled={disabled} />
            </Stack>
        </Stack>
    )
}

type KeyValue = {
    color?: string
    label: string
    total?: boolean
    percentage?: number
    value: string
    disabled?: boolean
}

function TenantListKeyValueRow({ color, label, total, percentage, value, disabled }: KeyValue) {
    const locale = useCurrentResolvedLocale()

    return (
        <Stack direction="row" gap={tokens.SizingSpacing075} alignItems="center" justifyContent="space-between">
            <Stack direction="row" gap={tokens.SizingSpacing075} alignItems="center">
                {total ? (
                    <Box width="10px" height="10px" />
                ) : (
                    <CircleIcon style={{ flexShrink: 0 }} size="10px" weight="fill" color={color} />
                )}
                <TextWithEllipsis
                    tooltipText={label}
                    typographyProps={{
                        variant: 'textS',
                        color: disabled ? tokens.ColorTextDisabled : tokens.ColorTextBold,
                        bold: total,
                    }}
                >
                    {label}
                </TextWithEllipsis>
                {percentage !== undefined && (
                    <div>
                        <Typography variant="textS" color={disabled ? tokens.ColorTextDisabled : tokens.ColorTextDefault} noWrap>
                            {new Percentage(percentage).format({ withUnit: true, locale })}
                        </Typography>
                    </div>
                )}
            </Stack>

            <Typography variant="textS" color={disabled ? tokens.ColorTextDisabled : tokens.ColorTextBoldest} bold noWrap>
                {value}
            </Typography>
        </Stack>
    )
}
